import { useMemo, useState } from "react";
import { History } from "@mui/icons-material";
import { Button, Chip, Tooltip, Typography } from "@mui/material";
import { DocumentReissueHistoryDialog } from "./DocumentReissueHistoryDialog";

type HistoryRow = Record<string, any>;

type UseDocumentHistoryColumnParams = {
  rowData: HistoryRow[];
  handleOpen?: () => void;
  setBinaryUrl?: (url: string) => void;
};

function rowHasHistory(row: HistoryRow): boolean {
  return (row?.reissueCount ?? 0) > 0 || !!row?.hasReissueHistory;
}

/**
 * Enriquece rows com data da última remissão + coluna Histórico
 * (dialog abre somente pela coluna Histórico).
 */
export function useDocumentHistoryColumn({
  rowData,
  handleOpen,
  setBinaryUrl,
}: UseDocumentHistoryColumnParams) {
  const [historyRow, setHistoryRow] = useState<HistoryRow | null>(null);

  const displayRows = useMemo(
    () =>
      (rowData || []).map((row: HistoryRow) => {
        const showHistory = rowHasHistory(row);
        return {
          ...row,
          // Apenas a data — o dialog abre só na coluna Histórico
          lastDateCell: showHistory ? (
            <Typography
              component="span"
              variant="body2"
              fontSize="0.8125rem"
              noWrap
            >
              {row.lastDate || row.date || "—"}
            </Typography>
          ) : (
            <Tooltip title="Este documento foi emitido apenas uma vez">
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                fontSize="0.8125rem"
              >
                —
              </Typography>
            </Tooltip>
          ),
          historyAction: showHistory ? (
            <Button
              variant="text"
              color="primary"
              startIcon={<History />}
              onClick={(event) => {
                event.stopPropagation();
                setHistoryRow(row);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 0,
                px: 1,
              }}
            >
              Histórico
            </Button>
          ) : (
            <Chip
              label="Emissão única"
              variant="outlined"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                borderColor: "divider",
              }}
            />
          ),
        } as HistoryRow;
      }) as HistoryRow[],
    [rowData],
  );

  const historyDialog = (
    <DocumentReissueHistoryDialog
      open={!!historyRow}
      onClose={() => setHistoryRow(null)}
      history={historyRow?.history}
      onViewBinary={
        handleOpen && setBinaryUrl
          ? (url) => {
              setBinaryUrl(url);
              handleOpen();
            }
          : undefined
      }
    />
  );

  const openHistory = (row: HistoryRow) => {
    if (rowHasHistory(row)) setHistoryRow(row);
  };

  return { displayRows, historyDialog, openHistory, rowHasHistory };
}
